/* eslint-disable react-hooks/exhaustive-deps */
// cspell: ignore Timelapse
import { ThingEventSnapshot } from '@eagle/core-data-types';
import { TimeZone } from '@eagle/data-function-types';
import { Button, Chip, Link, Stack, styled, Typography, TypographyProps } from '@ethings/ui-toolkit';
import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, timelineItemClasses, TimelineSeparator } from '@mui/lab';
import { Box, useTheme } from '@mui/material';
import { isNumber } from 'lodash';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import React, {
  FC,
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { I18n } from '../../providers/i18n';
import { Maybe, Nullable, Undefinable } from '../../types';
import { FormatTimestamp } from '../format';
import { CircleIcon, Icon } from '../icons';
import { DateTimeTooltip } from '../tooltip';

interface StyledChipProps {
  maxHeight?: string;
}

const LINE_HEIGHT = '1.0625rem';

const StyledChip = styled(Chip)<StyledChipProps>(({ maxHeight }) => ({
  maxHeight,
}));

export interface TimelineData {
  color: string;
  date?: Date;
  eventId?: string;
  primary: string;
  secondary?: string;
  secondaryChip?: string;
  timeDifference?: Duration;
  timezone?: TimeZone;
  isSummarized?: boolean;
  eventCount?: number;
  originalEvents?: ThingEventSnapshot[];
  summarizationMode?: string;
  summarizedEvent?: any;
}

interface Props {
  cursor?: number;
  data: TimelineData[];
  'data-testid'?: string;
  disableFocus?: boolean;
  hoveredEventId?: Maybe<string>;
  minTimeDifference?: DurationLikeObject;
  onClick?: (index: number) => void;
  onMouseEnter?: (eventId: Undefinable<string>) => void;
  onMouseLeave?: (eventId: Undefinable<string>) => void;
  onSelected?: (index: number) => void;
  preCalculatedDateDiff?: boolean;
  showCursorLocation?: boolean;
  startTime?: Date;
  onSummarizedEventClick?: (events: any[], summarizedEvent?: any) => void;
}

const dropZero = (input: Duration): Duration => {
  const normalized = input.normalize();
  const { hours, minutes, seconds } = normalized.shiftTo('hours', 'minutes', 'seconds');
  const handleZero = (number: number): Undefinable<number> => number < 1 ? undefined : Math.round(number);
  return Duration.fromObject({ hours: handleZero(hours), minutes: handleZero(minutes), seconds: handleZero(seconds) }, { locale: I18n.getInstance().language });
};

const elapsesMinTimeDifference = (minTimeDifference?: DurationLikeObject, timeDifference?: Duration): boolean => {
  if (!timeDifference || !minTimeDifference) return false;
  const { hours, minutes, seconds } = minTimeDifference;
  const minTimeSeconds = Duration.fromObject({ hours, minutes, seconds }).as('milliseconds');
  const timeOfEvent = timeDifference.as('milliseconds');
  return timeOfEvent >= minTimeSeconds;
};

export const TimeLine: FC<Props> = ({
  cursor: controllableCursor,
  data,
  disableFocus,
  hoveredEventId,
  minTimeDifference = { minutes: 5 },
  onClick,
  onMouseEnter,
  onMouseLeave,
  onSelected,
  onSummarizedEventClick,
  preCalculatedDateDiff,
  showCursorLocation,
  startTime,
  ...props
}) => {
  const [cursor, setCursor] = useState(-1);
  const theme = useTheme();
  const timeZone = !preCalculatedDateDiff ? data.find(({ timezone }) => !!timezone)?.timezone : undefined;

  const isColored = (i: number): boolean => cursor === i && !!showCursorLocation;

  const dataWithDifference: TimelineData[] = useMemo(() => {
    return !preCalculatedDateDiff
      ? data.map(({ date, ...current }, i) => {
        const prevDate = data[i - 1]?.date;
        if (!prevDate || !date || i === 0) return { date, ...current, timezone: timeZone };
        return {
          ...current,
          timezone: current.timezone ?? timeZone,
          date,
          timeDifference: DateTime.fromJSDate(date).diff(DateTime.fromJSDate(prevDate)),
        };
      })
      : data;
  }, [timeZone, data, preCalculatedDateDiff]);

  useEffect(() => {
    if (!isNumber(controllableCursor)) return;
    setCursor(controllableCursor);
  }, [controllableCursor]);

  const keyboardHandler = ({ key }: KeyboardEvent<HTMLUListElement>): void => {
    if (!isNumber(controllableCursor)) return;
    switch (key) {
      case 'ArrowUp': {
        if (cursor - 1 < 0) return setCursor(data.length - 1);
        setCursor((prev) => prev - 1);
        break;
      }
      case 'ArrowDown': {
        if (cursor + 1 > data.length - 1) return setCursor(0);
        setCursor((prev) => prev + 1);
        break;
      }
    }
  };

  const renderSeparator = (timeDifference: Duration): JSX.Element => {
    const minTimeCheck = elapsesMinTimeDifference(minTimeDifference, timeDifference);
    if (!minTimeCheck) return <></>;

    return (
      <TimelineItem sx={{ minHeight: '60px' }}>
        <TimelineSeparator>
          <TimelineDot sx={{ backgroundColor: 'transparent', boxShadow: 'none', p: 0, zIndex: 1 }}>
            <Icon data-testid="clock-icon" icon="Clock" style={{ color: theme.palette.grey[400], height: '.8em', width: '.8em' }} />
          </TimelineDot>
          <TimelineConnector sx={{
            backgroundColor: 'transparent',
            borderLeft: `2px dotted ${theme.palette.grey[400]}`,
            transform: 'scaleY(2.5)',
            zIndex: 0,
          }}
          />
        </TimelineSeparator>
        <TimelineContent color="text.secondary" sx={{ m: 0, p: 0 }}>
          <Typography variant="subtitle2" ml=".7rem" mt=".9rem">
            <i>{dropZero(timeDifference).toHuman()}</i>
          </Typography>
        </TimelineContent>
      </TimelineItem>
    );
  };

  const onFocusCapture = useCallback((index: number) => {
    if (controllableCursor) setCursor(index);
    onSelected?.(index);
  }, []);

  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    for (let i = 0; i < dataWithDifference.length; i++) {
      const ref = refs.current[i];
      if (cursor === i && !disableFocus || hoveredEventId === dataWithDifference[i].eventId) {
        ref?.focus();
      }
      else {
        ref?.blur();
      }
    }
  }, [cursor, hoveredEventId, dataWithDifference, disableFocus]);

  const buttonRef = useCallback((element: Nullable<HTMLButtonElement>, index: number) => {
    refs.current[index] = element;
  }, []);

  return (
    // Using a Box instead of Timeline because the Timeline in
    // @mui/lab@5.0.0-alpha.53 has a performance issue when rendering a lot of
    // children. In more recent versions this is fixed.
    <Box
      component="ul"
      sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
        '&': {
          m: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        },
      }}
      onKeyDown={keyboardHandler}
    >
      {(startTime && dataWithDifference[0].date)
        && renderSeparator(DateTime.fromJSDate(dataWithDifference[0].date).diff(DateTime.fromJSDate(startTime)))
      }
      {dataWithDifference.map((timelineData, i) => {
        const nextItemDifference = elapsesMinTimeDifference(minTimeDifference, dataWithDifference[i + 1]?.timeDifference);

        return (
          <MemoizedItem
            key={i}
            buttonRef={buttonRef}
            data-testid={props['data-testid']}
            disableFocusRipple={disableFocus}
            hasSeparator={elapsesMinTimeDifference(minTimeDifference, timelineData.timeDifference)}
            index={i}
            isColored={isColored(i)}
            isHovered={hoveredEventId === timelineData.eventId}
            isLast={i === dataWithDifference.length - 1}
            nextItemDifference={nextItemDifference}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocusCapture={onFocusCapture}
            onClick={onClick}
            onSummarizedEventClick={onSummarizedEventClick}
            {...timelineData}
          />
        );
      })}
    </Box>
  );
};

interface ItemProps extends TimelineData {
  buttonRef?: ((instance: HTMLButtonElement | null, index: number) => void);
  'data-testid'?: string;
  disableFocusRipple?: boolean;
  hasSeparator: boolean;
  index: number;
  isLast: boolean;
  isHovered: boolean;
  isColored: boolean;
  nextItemDifference: boolean;
  onClick?: (index: number) => void;
  onMouseEnter?: (eventId: Undefinable<string>) => void;
  onFocusCapture?: (index: number) => void;
  onMouseLeave?: (eventId: Undefinable<string>) => void;
  onSummarizedEventClick?: (events: any[], summarizedEvent?: any) => void;
  isSummarized?: boolean;
  eventCount?: number;
  originalEvents?: any[];
  summarizationMode?: string;
}

interface TimelineItemButtonProps {
  itemColor: string;
  isColored: boolean;
  isHovered: boolean;
}

const TimelineItemButton = styled(Button)<TimelineItemButtonProps>(({ theme, itemColor, isColored, isHovered }) => {
  const backgroundColor = isColored ? itemColor : isHovered ? '#0299d609' : 'transparent';

  return {
    alignItems: 'flex-start',
    backgroundColor,
    color: isColored ? theme.palette.getContrastText(itemColor) : 'inherit',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.125rem',
    textTransform: 'inherit',
    width: '100%',
    '&:hover': { backgroundColor },
    '&:focus-visible': { backgroundColor },
    '&:active': { backgroundColor },
  };
});

const Item: FC<ItemProps> = ({
  buttonRef,
  color,
  date,
  disableFocusRipple,
  eventId,
  eventCount,
  hasSeparator,
  index,
  isLast,
  isColored,
  isHovered,
  isSummarized,
  nextItemDifference,
  onMouseEnter,
  onMouseLeave,
  onFocusCapture,
  onClick,
  onSummarizedEventClick,
  originalEvents,
  primary,
  secondary,
  secondaryChip,
  summarizationMode,
  summarizedEvent,
  timezone,
  timeDifference,
  ...props
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const displaySx: TypographyProps = { lineHeight: LINE_HEIGHT, textAlign: 'left', width: '75%' };

  const renderDateTime = (dateItem: Date, timezone?: TimeZone): JSX.Element => {
    const zoneName = timezone?.id;
    const when = DateTime.fromJSDate(dateItem, { zone: zoneName }).setLocale(navigator.language);
    const whenLocal = when.toLocal();
    const localTimeFormat = when.day !== whenLocal.day ? DateTime.DATETIME_MED_WITH_SECONDS : DateTime.TIME_WITH_SECONDS;
    const localTimeString = when.toLocaleString({ ...localTimeFormat });

    return (
      <DateTimeTooltip
        disableHoverListener={whenLocal.offsetNameShort === when.offsetNameShort}
        display={
          <Stack>
            <Typography component="span" color={theme.palette.text.secondary} lineHeight={LINE_HEIGHT} variant="caption">
              {whenLocal.toFormat('dd LLL')}
            </Typography>
            <Typography component="span" variant="body1" lineHeight={LINE_HEIGHT}>
              <FormatTimestamp format="timeWithSeconds" value={dateItem} />
            </Typography>
          </Stack>
        }
        placement="top"
        sx={{ textAlign: 'right', overflow: 'unset' }}
        title={t('common:component.alert-table.hint.tooltip', { dateTime: localTimeString, offsetName: when.offsetNameShort })}
      />
    );
  };

  const renderSeparator = (timeDifference: Duration): JSX.Element => {
    return (
      <TimelineItem sx={{ minHeight: '60px' }}>
        <TimelineSeparator>
          <TimelineDot sx={{ backgroundColor: 'transparent', boxShadow: 'none', p: 0, zIndex: 1 }}>
            <Icon data-testid="clock-icon" icon="Clock" style={{ color: theme.palette.grey[400], height: '.8em', width: '.8em' }} />
          </TimelineDot>
          <TimelineConnector sx={{
            backgroundColor: 'transparent',
            borderLeft: `2px dotted ${theme.palette.grey[400]}`,
            transform: 'scaleY(2.5)',
            zIndex: 0,
          }}
          />
        </TimelineSeparator>
        <TimelineContent color="text.secondary" sx={{ m: 0, p: 0 }}>
          <Typography variant="subtitle2" ml=".7rem" mt=".9rem">
            <i>{dropZero(timeDifference).toHuman()}</i>
          </Typography>
        </TimelineContent>
      </TimelineItem>
    );
  };

  return (
    <Fragment>
      {hasSeparator && timeDifference && renderSeparator(timeDifference)}
      <TimelineItem data-testid={props['data-testid']} sx={{ minHeight: '60px' }}>
        <TimelineSeparator>
          <TimelineDot
            sx={{
              backgroundColor: color,
              boxShadow: 'none',
              p: 0,
              zIndex: 1,
            }}
          >
            <CircleIcon data-testid="circle-icon" sx={{ color, height: '.8em', width: '.8em' }} />
          </TimelineDot>
          {!isLast
            && <TimelineConnector
              sx={{
                backgroundColor: nextItemDifference ? 'transparent' : theme.palette.grey[400],
                borderLeft: nextItemDifference ? `2px dotted ${theme.palette.grey[400]}` : theme.palette.grey[400],
                transform: 'scaleY(2.5)',
                zIndex: 0,
              }}
            />
          }
        </TimelineSeparator>
        <TimelineContent sx={{ p: 0, ml: '.25rem' }}>
          <TimelineItemButton
            data-testid={`item-${index}-button`}
            disableFocusRipple={disableFocusRipple}
            tabIndex={disableFocusRipple ? -1 : 0}
            onClick={() => onClick?.(index)}
            onFocusCapture={() => { onFocusCapture?.(index); }}
            onMouseEnter={() => { onMouseEnter?.(eventId); }}
            onMouseLeave={() => { onMouseLeave?.(eventId); }}
            ref={(element) => { buttonRef?.(element, index); }}
            itemColor={color}
            isColored={isColored}
            isHovered={isHovered}
          >
            <Stack width="75%">
              <Typography data-testid={`item-${index}-primary`} {...displaySx}>{primary}</Typography>
              <Stack direction="row" spacing={theme.spacing(1)}>
                {secondary
                  && <Typography
                    data-testid={`item-${index}-secondary`}
                    variant="body2"
                    lineHeight={LINE_HEIGHT}
                    textAlign="left"
                  >
                    {secondary}
                  </Typography>
                }
                {secondaryChip
                  && <StyledChip
                    data-testid={`item-${index}-secondary-chip`}
                    label={secondaryChip}
                    variant="outlined"
                    size="small"
                    maxHeight={LINE_HEIGHT}
                  />
                }
              </Stack>
              {isSummarized && eventCount && eventCount > 1 && summarizationMode && (
                <Stack direction="row">
                  <Typography component="div" variant="caption">
                    <Trans
                      i18nKey={`track:page.history.showing-${summarizationMode}-count-of-occurrences.labels`}
                      values={{ count: eventCount }}
                      components={[
                        <Link
                          key={0}
                          component="span"
                          variant="caption"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSummarizedEventClick?.(originalEvents || [], summarizedEvent);
                          }}
                        />,
                      ]}
                    />
                  </Typography>
                </Stack>
              )}
            </Stack>
            {date && renderDateTime(date, timezone)}
          </TimelineItemButton>
        </TimelineContent>
      </TimelineItem>
    </Fragment>
  );
};

const MemoizedItem = React.memo(Item);
